/**
 * RunTutorAccessPanel — NC-1589 (Epic #1586).
 *
 * Operator standard since 2026-10-08: every run gets a tutor AD account that is
 * local Administrator on ALL trainee VMs (120/130, NC-1587), so the tutor can
 * install software during a training without owning a VM. This card makes that
 * account usable: login, credentials (via the existing Welcome Card, audited),
 * and an RDP target per trainee VM.
 *
 * Visible to operators+ (the parent decides). The password is never part of the
 * run doc — "Zugangsdaten anzeigen" fetches it on demand like a trainee card.
 */

import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Run } from '../services/types';
import { downloadTextFile } from '../utils/downloadTextFile';

type TutorRun = Pick<Run, 'runId' | 'status' | 'tutor' | 'tutorAccount' | 'accounts'>;

interface RunTutorAccessPanelProps {
  run: TutorRun;
  /** Opens the Welcome Card dialog for the given SAM (fetches the KV password). */
  onShowCredentials: (sam: string) => void;
}

/** "NETBIOS\\sam" from the UPN suffix — mirrors deriveDomainLogin in runs-welcome. */
export function tutorDomainLogin(upn: string | undefined, sam: string): string {
  const label = (upn ?? '').split('@')[1]?.split('.')[0] ?? '';
  return label ? `${label.toUpperCase()}\\${sam}` : sam;
}

/** Minimal .rdp file that pre-fills the tutor login and still asks for the password. */
export function buildTutorRdpFile(address: string, login: string): string {
  return [
    `full address:s:${address}:3389`,
    `username:s:${login}`,
    'prompt for credentials:i:1',
    'screen mode id:i:2',
  ].join('\r\n');
}

const PENDING_STATUSES = ['planned', 'provisioning'];

export function RunTutorAccessPanel({ run, onShowCredentials }: RunTutorAccessPanelProps) {
  const ta = run.tutorAccount;

  if (!ta) {
    const pending = !!run.tutor && PENDING_STATUSES.includes(String(run.status ?? ''));
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Tutor-Zugang
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {pending
              ? `Das Tutor-Konto für ${run.tutor?.name} wird bei der Bereitstellung angelegt.`
              : 'Für diese Schulung gibt es kein Tutor-Konto (angelegt vor Einführung des Tutor-Kontos).'}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const login = tutorDomainLogin(ta.upn, ta.sam);
  const vms = (run.accounts ?? [])
    .filter((a) => !!a.vm?.vmName)
    .map((a) => ({
      vmName: a.vm?.vmName ?? '',
      trainee: [a.firstName, a.lastName].filter(Boolean).join(' ') || a.sam,
      address: a.vm?.publicIp || a.vm?.privateIp || '',
      isPublic: !!a.vm?.publicIp,
    }));

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Tutor-Zugang
        </Typography>

        <Typography variant="body2" sx={{ mb: 1 }}>
          {[ta.firstName, ta.lastName].filter(Boolean).join(' ')}
          {ta.email ? ` (${ta.email})` : ''} — Login{' '}
          <Box component="span" sx={{ fontFamily: 'monospace' }} data-testid="tutor-login">
            {login}
          </Box>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Administrator auf allen Teilnehmer-VMs dieser Schulung — für Software-Installationen.
          {ta.isParticipant ? ' Zusätzlich gehört dem Tutor eine eigene Teilnehmer-VM.' : ''}
        </Typography>

        <Box sx={{ mt: 2 }}>
          <Button variant="outlined" onClick={() => onShowCredentials(ta.sam)}>
            Zugangsdaten anzeigen
          </Button>
        </Box>

        <Alert severity="warning" sx={{ mt: 2 }}>
          Windows erlaubt nur eine Anmeldung pro VM: Meldet sich der Tutor während der Schulung auf einer
          Teilnehmer-VM an, wird der Teilnehmer getrennt.
        </Alert>

        {vms.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Noch keine Teilnehmer-VMs vorhanden.
          </Typography>
        ) : (
          <Table size="small" sx={{ mt: 2 }} aria-label="Teilnehmer-VMs für den Tutor-Zugang">
            <TableHead>
              <TableRow>
                <TableCell>VM</TableCell>
                <TableCell>Teilnehmer</TableCell>
                <TableCell>Adresse</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {vms.map((vm) => (
                <TableRow key={vm.vmName}>
                  <TableCell>{vm.vmName}</TableCell>
                  <TableCell>{vm.trainee}</TableCell>
                  <TableCell>{vm.address ? `${vm.address}${vm.isPublic ? '' : ' (intern)'}` : '—'}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      disabled={!vm.address}
                      aria-label={`RDP-Datei für ${vm.vmName}`}
                      onClick={() =>
                        downloadTextFile(`Tutor-${vm.vmName}.rdp`, buildTutorRdpFile(vm.address, login), 'application/x-rdp')
                      }
                    >
                      .rdp
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

export default RunTutorAccessPanel;
